import { axiosClient } from '@/api/http/axios-client';
import { syncBillingBalance } from '@/query/billing/billing.cache';
import type {
  CreateDocumentParams,
  FacadeResponse,
  FirestoreCollection,
  FirestoreCollectionsResponse,
  FirestoreDocumentResponse,
  FirestoreDocumentWithMetadata,
  QueryDocumentsParams,
  SubcollectionsResponse,
  UpdateDocumentParams,
} from './firebase.interface';
import { getSchema, setSchema } from './schema-cache';

async function getCollections(connectionId: string): Promise<FirestoreCollection[]> {
  const { data } = await axiosClient.get<FirestoreCollectionsResponse>(
    `/firestore/collections?connectionId=${connectionId}`
  );
  syncBillingBalance(data.balanceAfter);
  return data.collections;
}

async function getDocument(
  connectionId: string,
  collectionPath: string,
  documentId: string
): Promise<FirestoreDocumentWithMetadata> {
  const encoded = encodeURIComponent(collectionPath);
  const { data } = await axiosClient.get<FirestoreDocumentResponse>(
    `/firestore/collections/${encoded}/documents/${documentId}?connectionId=${connectionId}`
  );
  syncBillingBalance(data.balanceAfter);
  return data.document;
}

async function queryDocuments(
  connectionId: string,
  collectionPath: string,
  body: QueryDocumentsParams
): Promise<FacadeResponse> {
  const encoded = encodeURIComponent(collectionPath);
  const cached = getSchema(connectionId, collectionPath);
  const requestBody: QueryDocumentsParams = cached
    ? { ...body, schema: cached.schema, schemaCachedAt: cached.cachedAt }
    : { ...body };

  const { data } = await axiosClient.post<FacadeResponse>(
    `/firestore/collections/${encoded}/query`,
    requestBody
  );

  syncBillingBalance(data.balanceAfter);
  setSchema(connectionId, collectionPath, data.schema);
  return data;
}

async function validateQueryPreview(
  connectionId: string,
  collectionPath: string,
  body: QueryDocumentsParams
): Promise<FacadeResponse> {
  const encoded = encodeURIComponent(collectionPath);
  const cached = getSchema(connectionId, collectionPath);
  const requestBody: QueryDocumentsParams = cached
    ? { ...body, schema: cached.schema, schemaCachedAt: cached.cachedAt }
    : { ...body };

  const { data } = await axiosClient.post<FacadeResponse>(
    `/firestore/collections/${encoded}/query/validate`,
    requestBody
  );

  setSchema(connectionId, collectionPath, data.schema);
  return data;
}

async function createDocument(
  _connectionId: string,
  collectionPath: string,
  body: CreateDocumentParams
): Promise<void> {
  const encoded = encodeURIComponent(collectionPath);
  await axiosClient.post(`/firestore/collections/${encoded}/documents`, body);
}

async function updateDocument(
  connectionId: string,
  collectionPath: string,
  documentId: string,
  body: UpdateDocumentParams
): Promise<void> {
  const encoded = encodeURIComponent(collectionPath);
  await axiosClient.put(
    `/firestore/collections/${encoded}/documents/${documentId}?connectionId=${connectionId}`,
    body
  );
}

async function deleteDocument(
  connectionId: string,
  collectionPath: string,
  documentId: string
): Promise<void> {
  const encoded = encodeURIComponent(collectionPath);
  await axiosClient.delete(
    `/firestore/collections/${encoded}/documents/${documentId}?connectionId=${connectionId}`
  );
}

async function getSubcollections(
  connectionId: string,
  documentPath: string
): Promise<SubcollectionsResponse> {
  const encoded = encodeURIComponent(documentPath);
  const { data } = await axiosClient.get<SubcollectionsResponse>(
    `/firestore/document/${encoded}/subcollections?connectionId=${connectionId}`
  );
  return data;
}

export const FirebaseApi = {
  getCollections,
  getDocument,
  queryDocuments,
  validateQueryPreview,
  createDocument,
  updateDocument,
  deleteDocument,
  getSubcollections,
};
